import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, MousePointer2 } from 'lucide-react';
import ShotNumber from '../../components/treatment/ShotNumber';
import GearTag from '../../components/treatment/GearTag';
import { shotNumbers } from '../../lib/treatmentShots';
import { cn } from '../../lib/utils';
import { Reference } from './references';
import { placeIn, prefersReducedMotion, usePlayOnView } from './motion';

// A treatment being built: reference frames are dragged out of a web page and
// dropped onto the shots that need them, as the app takes a frame from
// Frameset or any site. Each shot's gear appears, and the gear goes to the
// quote. Made-up shots with no client; the frames are the helix's, and the
// numbering, the shot number box and the gear pills are the app's own.

const SHOTS = [
  { id: 'a', title: 'Through the dust', meta: 'Wide · Tracking · 0:08', frame: 'dust', gear: [{ name: 'Alexa Mini LF' }, { name: 'Zeiss 50mm' }] },
  { id: 'b', title: 'The ridge at last light', meta: 'Aerial · Push in · 0:12', frame: 'summit', gear: [{ name: 'Drone and pilot', hire: true }] },
  { id: 'c', parent_id: 'b', title: 'The walk past the posts', meta: 'Wide · Locked off · 0:10', frame: 'posts', gear: [{ name: 'SmallHD Cine 7' }, { name: 'Teradek Bolt' }] },
];
// A web page of frames, most of which no shot uses, as a real one would be.
// The three the shots take are each on it once, so a drag starts from one place.
const TRAY = ['dust', 'city', 'swimmer', 'snow', 'posts', 'wine', 'roadside', 'rain', 'summit', 'stable'];
const NUMBERS = shotNumbers(SHOTS);

const START = 600;
const PER_SHOT = 1400; // pick up, carry, drop, then its gear
const CARRY_MS = 800;

export function ShotListDrop() {
  const stageRef = useRef(null);
  const trayRefs = useRef({});
  const slotRefs = useRef([]);
  const seen = usePlayOnView(stageRef, 0.4);
  const [instant] = useState(prefersReducedMotion);
  // carrying: which shot's frame is in hand, and whether it has left the tray
  const [state, setState] = useState({ carrying: null, moving: false, placed: 0, gear: 0, done: false });

  useEffect(() => {
    if (!seen) {
      if (!instant) setState({ carrying: null, moving: false, placed: 0, gear: 0, done: false });
      return undefined;
    }
    if (instant) {
      setState({ carrying: null, moving: false, placed: SHOTS.length, gear: SHOTS.length, done: true });
      return undefined;
    }
    const set = (patch) => setState((prev) => ({ ...prev, ...patch }));
    const at = [];
    SHOTS.forEach((_, i) => {
      const t = START + i * PER_SHOT;
      at.push([t, { carrying: i, moving: false }]);
      at.push([t + 120, { moving: true }]);
      at.push([t + 120 + CARRY_MS, { carrying: null, moving: false, placed: i + 1 }]);
      at.push([t + 120 + CARRY_MS + 250, { gear: i + 1 }]);
    });
    at.push([START + SHOTS.length * PER_SHOT + 300, { done: true }]);
    const timers = at.map(([ms, patch]) => setTimeout(() => set(patch), ms));
    return () => timers.forEach(clearTimeout);
  }, [seen, instant]);

  // Where each frame sits on the tray and where its shot's slot is
  const [places, setPlaces] = useState(null);
  const measure = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    setPlaces(SHOTS.map((shot, i) => {
      const from = trayRefs.current[shot.frame];
      const to = slotRefs.current[i];
      return from && to ? { from: placeIn(from, stage), to: placeIn(to, stage) } : null;
    }));
  }, []);
  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const { carrying, moving, placed, gear, done } = state;
  const usedFrames = new Set(SHOTS.slice(0, placed).map((s) => s.frame));
  if (carrying !== null) usedFrames.add(SHOTS[carrying].frame);
  const carry = carrying !== null && places?.[carrying];

  return (
    <div ref={stageRef} className="relative rounded-highlight bg-surface p-5 sm:p-6 flex flex-col gap-5">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-xl font-normal">Shot list</h3>
        <span className="text-sm text-muted-foreground">{SHOTS.length} shots · 0:30</span>
      </div>

      <div className="grid gap-5 sm:gap-6 sm:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        {/* The tray of reference frames */}
        <div className="rounded-cards bg-background p-2 flex flex-col gap-2 self-start w-full">
          <div className="flex items-center gap-1.5 px-1" aria-hidden="true">
            <span className="w-2 h-2 rounded-full bg-foreground/20" />
            <span className="w-2 h-2 rounded-full bg-foreground/20" />
            <span className="w-2 h-2 rounded-full bg-foreground/20" />
            <span className="ml-1 flex-1 truncate rounded-pill bg-mist px-2 py-0.5 text-[11px] text-muted-foreground">any website</span>
          </div>
          {/* Small thumbnails in a grid, like a page of stills */}
          <ul className="grid grid-cols-5 sm:grid-cols-3 lg:grid-cols-4 gap-1.5" aria-label="Frames on a web page">
            {TRAY.map((scene, i) => (
              <li
                key={`${scene}-${i}`}
                ref={(el) => { if (!trayRefs.current[scene] || TRAY.indexOf(scene) === i) trayRefs.current[scene] = el; }}
                className={cn(
                  'rounded-small overflow-hidden aspect-square transition-opacity duration-300',
                  usedFrames.has(scene) && TRAY.indexOf(scene) === i ? 'opacity-25' : 'opacity-100',
                )}
              >
                <Reference name={scene} className="w-full h-full block" />
              </li>
            ))}
          </ul>
        </div>

        {/* The shot list */}
        <ol className="flex flex-col gap-4 min-w-0" aria-label="Shot list">
          {SHOTS.map((shot, i) => (
            <li key={shot.id} className={cn('flex gap-3 min-w-0', shot.parent_id && 'pl-6')}>
              <div
                ref={(el) => { slotRefs.current[i] = el; }}
                className={cn(
                  'w-[112px] shrink-0 self-start aspect-video rounded-cards overflow-hidden',
                  i < placed ? '' : 'border-2 border-dashed border-line-strong',
                )}
              >
                {i < placed && <Reference name={shot.frame} className={cn('w-full h-full block', !instant && 'animate-in zoom-in-95 duration-200')} />}
              </div>
              <div className="flex flex-col gap-1.5 min-w-0">
                <p className="flex items-center gap-2 min-w-0">
                  <ShotNumber number={NUMBERS[shot.id]} />
                  <span className="truncate">{shot.title}</span>
                </p>
                <p className="text-sm text-muted-foreground truncate">{shot.meta}</p>
                <ul className={cn('flex flex-wrap gap-1.5 transition-opacity duration-300', i < gear ? 'opacity-100' : 'opacity-0')} aria-hidden={i >= gear}>
                  {shot.gear.map((g) => <GearTag key={g.name} name={g.name} hire={g.hire} />)}
                </ul>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4 text-sm">
        <span className="text-muted-foreground">Own gear filled, hires outlined</span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 transition-colors duration-500',
            done ? 'bg-moss-soft text-moss' : 'bg-mist text-muted-foreground',
          )}
        >
          {done && <Check className="w-4 h-4" />}
          {done ? 'Gear added to the quote' : 'Gear to the quote'}
        </span>
      </div>

      {/* The frame in hand, carried from the tray to its shot */}
      {carry && (
        <div
          className="pointer-events-none absolute left-0 top-0 z-10 origin-top-left ease-[cubic-bezier(.3,.7,.25,1)]"
          style={{
            width: carry.to.w,
            transform: moving
              ? `translate(${carry.to.x}px, ${carry.to.y}px) scale(1) rotate(0deg)`
              : `translate(${carry.from.x}px, ${carry.from.y}px) scale(${carry.from.w / carry.to.w}) rotate(-4deg)`,
            transition: moving ? `transform ${CARRY_MS}ms` : 'none',
          }}
          aria-hidden="true"
        >
          <div className="rounded-cards overflow-hidden aspect-video shadow-modal">
            <Reference name={SHOTS[carrying].frame} className="w-full h-full block" />
          </div>
          <MousePointer2 className="absolute -right-3 -bottom-3 w-6 h-6 fill-foreground text-background" />
        </div>
      )}
    </div>
  );
}
